import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { GRADOS } from '../../constants';

function EditStudentModal({ student, onClose, onUpdate }) {
    const [editedData, setEditedData] = useState({ ...student });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onUpdate(editedData);
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all">
            <div className="glass-card w-full max-w-lg p-8 rounded-2xl shadow-2xl animate-slide-up">
                <h2 className="text-2xl font-bold mb-6 text-slate-800">Editar Estudiante</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-slate-600 font-semibold mb-1">Nombre Completo</label>
                        <input type="text" name="nombreCompleto" value={editedData.nombreCompleto} onChange={handleChange} className="glass-input w-full px-4 py-2 rounded-xl" required />
                    </div>
                    <div>
                        <label className="block text-slate-600 font-semibold mb-1">Documento de Identidad</label>
                        <input type="text" name="documentoIdentidad" value={editedData.documentoIdentidad} onChange={handleChange} className="glass-input w-full px-4 py-2 rounded-xl" required />
                    </div>
                    <div>
                        <label className="block text-slate-600 font-semibold mb-1">Grado</label>
                        <select name="grado" value={editedData.grado} onChange={handleChange} className="glass-input w-full px-4 py-2 rounded-xl">
                            {GRADOS.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                    </div>
                    <div className="flex justify-end space-x-3 mt-8">
                        <button type="button" onClick={onClose} className="btn-secondary px-6 py-2 rounded-xl">Cancelar</button>
                        <button type="submit" className="btn-primary px-6 py-2 rounded-xl">Guardar Cambios</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function StudentList({ user, currentYear }) {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGrade, setSelectedGrade] = useState('Todos');
    const [editStudent, setEditStudent] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!user || !currentYear) return;
        setLoading(true);
        // Escucha en tiempo real para obtener los estudiantes del usuario actual
        // QUERY UPDATE: We fetch ALL students for the teacher, then filter client-side for "Legacy" support
        // Ideal: .where("year", "==", currentYear) - but existing metrics don't have year.
        // STRATEGY: Fetch all, then filter in memory.
        const q = query(collection(db, "estudiantes"), where("profesorId", "==", user.uid));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const studentsList = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(student => {
                    // Logic: If student has a 'year', match it. If not, assume it's 2025 (Legacy).
                    const studentYear = student.year || 2025;
                    return studentYear === currentYear;
                });
            setStudents(studentsList);
            setLoading(false);
        }, (err) => {
            console.error("Error al obtener estudiantes:", err);
            setError("No se pudieron cargar los estudiantes.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user, currentYear]); // Re-run when year changes

    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de que quieres eliminar este estudiante?")) {
            try {
                await deleteDoc(doc(db, "estudiantes", id));
                setMessage("Estudiante eliminado correctamente.");
                setTimeout(() => setMessage(''), 3000);
            } catch (err) {
                setError("Error al eliminar el estudiante: " + err.message);
                setTimeout(() => setError(''), 3000);
            }
        }
    };

    const handleUpdate = async (updatedData) => {
        if (!editStudent) return;
        try {
            const studentRef = doc(db, "estudiantes", editStudent.id);
            const { id, ...dataToUpdate } = updatedData;
            await updateDoc(studentRef, dataToUpdate);
            setMessage("Información actualizada correctamente.");
            setTimeout(() => setMessage(''), 3000);
            setEditStudent(null);
        } catch (err) {
            setError("Error al actualizar: " + err.message);
            setTimeout(() => setError(''), 3000);
        }
    };

    const filteredStudents = selectedGrade === 'Todos'
        ? students
        : students.filter(s => s.grado === selectedGrade);

    // Sort students by name alphabetically
    filteredStudents.sort((a, b) => a.nombreCompleto.localeCompare(b.nombreCompleto));

    return (
        <div className="glass-card p-8 rounded-2xl animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <h2 className="text-2xl font-bold text-slate-800">
                    Listado de Estudiantes <span className="text-primary-600 text-lg ml-2">({currentYear})</span>
                </h2>
                <div className="relative z-10">
                    <select
                        value={selectedGrade}
                        onChange={(e) => setSelectedGrade(e.target.value)}
                        className="glass-input px-4 py-2 rounded-xl pr-10"
                    >
                        <option value="Todos">Todos los grados</option>
                        {GRADOS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                </div>
            </div>

            {message && <div className="mb-6 p-4 rounded-xl bg-green-50 text-green-700 font-medium border border-green-100 animate-pulse">{message}</div>}
            {error && <div className="mb-6 p-4 rounded-xl bg-red-50 text-red-700 font-medium border border-red-100 animate-pulse">{error}</div>}

            <div className="overflow-hidden rounded-xl border border-slate-200/50">
                {loading ? <div className="p-8 text-center text-slate-500">Cargando estudiantes...</div> : (
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-200/50 text-slate-500 uppercase text-xs tracking-wider">
                                <th className="p-4 font-bold">Nombre Completo</th>
                                <th className="p-4 font-bold">Documento</th>
                                <th className="p-4 font-bold">Grado</th>
                                <th className="p-4 font-bold text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredStudents.length > 0 ? (
                                filteredStudents.map(student => (
                                    <tr key={student.id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="p-4 font-medium text-slate-700">{student.nombreCompleto}</td>
                                        <td className="p-4 text-slate-500">{student.documentoIdentidad}</td>
                                        <td className="p-4">
                                            <span className={`px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wide 
                                                ${student.grado === 'Transición' ? 'bg-purple-100 text-purple-700' :
                                                    student.grado === 'Jardín' ? 'bg-pink-100 text-pink-700' :
                                                        'bg-blue-100 text-blue-700'}`}>
                                                {student.grado}
                                            </span>
                                        </td>
                                        <td className="p-4 text-center space-x-2">
                                            <button onClick={() => setEditStudent(student)} className="text-primary-600 hover:text-primary-800 font-medium text-sm bg-primary-50 px-3 py-1 rounded-lg transition-colors">Editar</button>
                                            <button onClick={() => handleDelete(student.id)} className="text-red-500 hover:text-red-700 font-medium text-sm bg-red-50 px-3 py-1 rounded-lg transition-colors">Eliminar</button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="p-8 text-center text-slate-400 italic">No hay estudiantes registrados en este año para el filtro seleccionado.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {editStudent && (
                <EditStudentModal
                    student={editStudent}
                    onClose={() => setEditStudent(null)}
                    onUpdate={handleUpdate}
                />
            )}
        </div>
    );
}
